import { round } from '@oliasoft-open-source/units';
import { ICampaignEstimate, ICampaignOperationSequenceItem } from 'src/common/interfaces/campaigns.interfaces';
import { HOURS_PER_DAY } from 'src/enums/general';

type TCampaignSection = {
  projectSectionId: string;
  sectionId?: string;
  name?: string;
  sectionName?: string;
  operationIds: string[];
};

export type TSectionRow = ICampaignOperationSequenceItem & {
  estimate: ICampaignEstimate;
  section: TCampaignSection;
  sectionIndex: number;
  estimatePosition: number;
  label: string;
};

type TSequenceTableRow = {
  sequenceIndex?: number;
};

const getOperationSequenceKey = (
  item: Pick<ICampaignOperationSequenceItem, 'campaignsEstimatesId' | 'projectSectionId'>,
) => `${item.campaignsEstimatesId}:${item.projectSectionId}`;

const getSectionLabel = (section: TCampaignSection, fallback?: string) =>
  section.name ?? section.sectionName ?? fallback ?? '';

export const buildSectionRows = (
  estimates: ICampaignEstimate[],
  operationSequence: ICampaignOperationSequenceItem[] = [],
): TSectionRow[] => {
  const savedPositionByKey = new Map(
    operationSequence.map((item, index) => [
      getOperationSequenceKey(item),
      Number.isInteger(item.position) ? item.position : index,
    ]),
  );

  return estimates
    .flatMap((estimate) => {
      const labels = estimate.simulation?.section_durations?.labels ?? [];
      return ((estimate.sections ?? []) as TCampaignSection[]).map((section, sectionIndex) => ({
        campaignId: estimate.campaignId,
        campaignsEstimatesId: estimate.campaignsEstimatesId,
        projectSectionId: section.projectSectionId,
        position: 0,
        estimate,
        section,
        sectionIndex,
        estimatePosition: estimate.position,
        label: getSectionLabel(section, labels[sectionIndex]),
      }));
    })
    .sort((a, b) => {
      const aPosition = savedPositionByKey.get(getOperationSequenceKey(a));
      const bPosition = savedPositionByKey.get(getOperationSequenceKey(b));

      if (aPosition !== undefined && bPosition !== undefined) {
        return aPosition - bPosition;
      }
      if (aPosition !== undefined) {
        return -1;
      }
      if (bPosition !== undefined) {
        return 1;
      }
      if (a.estimatePosition === b.estimatePosition) {
        return a.sectionIndex - b.sectionIndex;
      }
      return a.estimatePosition - b.estimatePosition;
    })
    .map((row, position) => ({ ...row, position }));
};

export const toSequencePayload = (rows: TSectionRow[]): ICampaignOperationSequenceItem[] =>
  rows.map((row, position) => ({
    campaignId: row.campaignId,
    campaignsEstimatesId: row.campaignsEstimatesId,
    projectSectionId: row.projectSectionId,
    position,
  }));

export const isEstimateSectionOrderValid = (rows: TSectionRow[]) => {
  const lastSectionIndexByEstimate = new Map<string, number>();

  return rows.every((row) => {
    const lastSectionIndex = lastSectionIndexByEstimate.get(row.campaignsEstimatesId);
    if (lastSectionIndex !== undefined && row.sectionIndex < lastSectionIndex) {
      return false;
    }

    lastSectionIndexByEstimate.set(row.campaignsEstimatesId, row.sectionIndex);
    return true;
  });
};

export const getReorderedSectionRows = (
  sectionRows: TSectionRow[],
  tableRows: TSequenceTableRow[],
  from: number,
  to: number,
) => {
  const fromIndex = tableRows[from]?.sequenceIndex;
  const toIndex = tableRows[to]?.sequenceIndex;

  if (fromIndex === undefined || toIndex === undefined) {
    return null;
  }

  const updatedRows = [...sectionRows];
  const [movedItem] = updatedRows.splice(fromIndex, 1);
  updatedRows.splice(toIndex, 0, movedItem);

  return updatedRows;
};

export const getDuration = (estimate: ICampaignEstimate, percentile: string, sectionIndex: number) => {
  const durations = estimate.simulation?.section_durations?.durations as Record<string, number[]> | undefined;

  return round(((durations?.[percentile]?.[sectionIndex] ?? 0) as number) / HOURS_PER_DAY, 2);
};

export const getOperationDuration = (estimate: ICampaignEstimate, percentile: string, operationIndex: number) => {
  const durations = estimate.simulation?.operation_durations?.durations as Record<string, number[]> | undefined;

  return round(((durations?.[percentile]?.[operationIndex] ?? 0) as number) / HOURS_PER_DAY, 2);
};
